import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Settings } from "lucide-react";
import ChallengeLoader from "components/Loaders/ChallengeLoader";
import {
  CircularProgressbar,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import openNotification from "components/utils/CustomNotification";
import dayjs from "dayjs";
import moment from "moment";
import duration from "dayjs/plugin/duration";
dayjs.extend(duration);



const ChallengeDetailPage = () => {
  const location = useLocation();
  const [challenge, setChallenge] = useState(() => {
    const locationState = location.state;
    if (locationState) {
      localStorage.setItem("selectedChallenge", JSON.stringify(locationState));
      return locationState;
    }

    const saved = localStorage.getItem("selectedChallenge");
    return saved ? JSON.parse(saved) : null;
  });
  console.log("From:", challenge?.from_datetime); // should print correctly
  console.log("To:", challenge?.to_datetime);

  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMoreDesc, setViewMoreDesc] = useState(false);
  const [viewMoreLB, setViewMoreLB] = useState(false);
  const [userProgress, setUserProgress] = useState(0);
  const [userProgressPercentage, setUserProgressPercentage] = useState(0);
  const [countdown, setCountdown] = useState("");
  const [challengeStatus, setChallengeStatus] = useState<"upcoming" | "live" | "ended">("upcoming");
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";
  const orgUid = localStorage.getItem("org_uid") || "";
  const userEntry = leaderboard.find((entry) => entry.user_uid === userUid);
  const [hasJoined, setHasJoined] = useState<boolean>(!!userEntry);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [motivIndex, setMotivIndex] = useState(0);

  const now = moment();
  const startDate = moment(challenge.from_datetime);
  const endDate = moment(challenge.to_datetime);
  const hasStarted = now.isSameOrAfter(startDate);
  const hasEnded = now.isAfter(endDate);

  let countdownText = "";
  if (!hasStarted) {
    const startIn = startDate.diff(now, 'days');
    countdownText = `Starts in ${startIn} day${startIn > 1 ? 's' : ''}`;
  } else if (!hasEnded) {
    const daysLeft = endDate.diff(now, 'days');
    countdownText = `Ends in ${daysLeft} day${daysLeft > 1 ? 's' : ''}`;
  } else {
    countdownText = `Ended on ${endDate.format("D MMMM")}`;
  }




  // console.log("Start:", challenge.start_datetime);
  // console.log("End:", challenge.end_datetime);
  // console.log("Now:", moment().toISOString());




  const beAddress =
    (window as any)._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;

  const formatSeconds = (sec: number): string => {
    const d = Math.floor(sec / (3600 * 24));
    const h = Math.floor((sec % (3600 * 24)) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;

    return `${d > 0 ? `${d}d ` : ""}${h}h ${m}m ${s}s`;
  };

  useEffect(() => {
    setHasJoined(!!userEntry);
  }, [leaderboard, userUid]);


  useEffect(() => {
    if (!challenge || leaderboard.length === 0) return;

    const userEntry = leaderboard.find((entry: any) => entry.user_uid === userUid);
    if (userEntry) {
      const achieved = userEntry.user_challenge_status_metrics || 0;
      const goal = challenge.goal_count || 1;
      const progressPercentage = Math.min(100, Math.round((achieved / goal) * 100));
      const progress = userEntry.user_challenge_status_metrics;
      setUserProgress(progress);
      setUserProgressPercentage(progressPercentage);
    }
  }, [leaderboard, challenge, userUid]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!challenge?.from_datetime || !challenge?.to_datetime) {
        setCountdown("Loading...");
        return;
      }

      try {
        const now = Date.now();
        const start = new Date(challenge.from_datetime).getTime();
        const end = new Date(challenge.to_datetime).getTime();

        if (now < start) {
          setChallengeStatus("upcoming");
          const seconds = Math.floor((start - now) / 1000);
          setCountdown(`Starts in ${formatSeconds(seconds)}`);
        } else if (now >= start && now < end) {
          setChallengeStatus("live");
          const seconds = Math.floor((end - now) / 1000);
          setCountdown(`Ends in ${formatSeconds(seconds)}`);
        } else {
          setChallengeStatus("ended");
          setCountdown("Challenge Ended");
        }
      } catch (e) {
        console.error("Countdown error:", e);
        setCountdown("Error calculating time");
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [challenge]);



  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await axios.get(`${beAddress}/api/challenge-leaderboard`, {
          headers: {
            "user-uid": userUid,
          }, withCredentials: true,
          params: {
            challenge_uid: challenge.challenge_uid,
          },
        });
        const lbArray = response.data.data;
        if (Array.isArray(lbArray) && lbArray.length > 0) {
          setLeaderboard(lbArray[0].leaderboard || []);
        } else {
          setLeaderboard([]);
        }

      } catch (error) {
        console.error("Leaderboard error:", error);
      } finally {
        setLoading(false);
      }
    };
    if (challenge?.challenge_uid) fetchLeaderboard();
  }, [challenge]);

  const topThree = leaderboard.slice(0, 3);
  const restLeaderboard = leaderboard.slice(3);
  if (loading) return <ChallengeLoader />;
  if (!challenge) return <div>Challenge not found.</div>;

  const renderUser = (entry?: any) => {
    if (!entry) return null;
    const name = entry.user_name || 'User';
    const metric = entry.user_challenge_status_metrics ?? 0;
    const icon = entry.icon_image;

    return (
      <>
        {icon ? (
          <UserAvatar src={`data:image/jpeg;base64,${icon}`} />
        ) : (
          <FallbackAvatar>{name.charAt(0).toUpperCase()}</FallbackAvatar>
        )}
        <strong>{name}</strong>
        <small>{metric} {challenge.metric_type}</small>
      </>
    );
  };



  const handleJoinChallenge = async (challenge: any) => {
    const payload = [
      { 
        challenge_uid: challenge.challenge_uid,
        user_uid: userUid,
        org_uid: orgUid,
      },
    ];

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/challenge-join`,
        payload,
        {
          headers: {
            accept: "application/json",
            "user-uid": userUid,
          }, withCredentials: true,
        }
      );

      if (response.data.status === "Success") {
        openNotification({
          type: "success",
          message: response.data.data || "Joined Challenge",
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        openNotification({
          type: "error",
          message: response.data.data || "Failed to join the challenge.",
        });
      }
    } catch (error: any) {
      openNotification({
        type: "error",
        message: error?.response?.data?.message || "Something went wrong. Please try again.",
      });
    }
  };


  const motivationalMessages = [
    (remaining: number, metric: string) =>
      `You're only ${remaining} ${metric} away from completing it. Don't give up now!`,
    (remaining: number, metric: string) =>
      `Consistency is key. Just ${remaining} ${metric} more to victory!`,
    (remaining: number, metric: string) =>
      `Almost there! Only ${remaining} ${metric} left. Stay strong!`
  ];
  if (loading) return <ChallengeLoader />;

  return (
    <>
      <Title0> Challenge Details</Title0>
      <Wrapper>
        <LeftSection>
          <TopSectionRow>
            <ChallengeImageWrapper>
              <ChallengeBadge>{challenge.challenge_name}</ChallengeBadge>
              <ChallengeImage
                src={`data:image/jpeg;base64,${challenge.image}`}
                alt="Challenge banner"
              />
              <ImageOverlay>
                Goal: {challenge.goal_count} {challenge.metric_type}
              </ImageOverlay>
              <StatusTag status={challengeStatus}>
                {challengeStatus === "upcoming"
                  ? "Yet to Start"
                  : challengeStatus === "live"
                    ? "Challenge Live!"
                    : "Challenge Ended"}
              </StatusTag>
            </ChallengeImageWrapper>
            <Description_Timer_Wrapper>
              <Title2>About this Challenge</Title2>
              <Description>
                {viewMoreDesc || challenge.challenge_desc?.length <= 100
                  ? challenge.challenge_desc
                  : `${challenge.challenge_desc?.slice(0, 100)}...`}
                {challenge.challenge_desc?.length > 100 && (
                  <Toggle onClick={() => setViewMoreDesc(!viewMoreDesc)}>
                    {viewMoreDesc ? "View Less" : "View More"}
                  </Toggle>
                )}
              </Description>

              <CountdownWrapper>
                <CountdownLabel>Status:</CountdownLabel>
                <CountdownText>{countdownText}</CountdownText>
              </CountdownWrapper>

            </Description_Timer_Wrapper>
          </TopSectionRow>


          {challengeStatus !== "ended" && (
            <MetaWrapper>
              <Title2>Your Progress in {challenge.challenge_name}</Title2>

              {hasJoined ? (
                <UserProgressContainer>
                  <UserInfo>
                    <Username>{userEntry?.user_name || userUid.split("@")[0]}</Username>
                    <UserMetric>{`${userProgress} / ${challenge.goal_count} ${challenge.metric_type}`}</UserMetric>
                  </UserInfo>
                  <MiniProgressWrapper>
                    <CircularProgressbar
                      value={userProgressPercentage}
                      text={`${userProgressPercentage}%`}
                      styles={buildStyles({
                        textSize: "18px",
                        pathColor: "#007bff",
                        textColor: "#333",
                        trailColor: "#eee",
                      })}
                    />
                  </MiniProgressWrapper>
                </UserProgressContainer>
              ) : (
                <JoinPlaceholder>
                  <p>Join this challenge to track your progress and compete with others!</p>
                </JoinPlaceholder>
              )}
            </MetaWrapper>
          )}

          {challengeStatus !== "ended" && (
            hasJoined ? (
              <QuitButton onClick={() => setShowConfirmModal(true)}>
                Quit Challenge
              </QuitButton>
            ) : (
              challenge.challenge_type !== "team" && (
                <JoinButton onClick={() => handleJoinChallenge(challenge)}>
                  Join Challenge
                </JoinButton>
              )
            )
          )}
        </LeftSection>

        <RightSection>

          <SectionTitle>Challenge Leaderboard 🏆 </SectionTitle>

          <PodiumContainer>
            <PodiumItem rank="2">
              {topThree[1] ? (
                <>
                  {renderUser(topThree[1])}
                  <MedalTag color="#c0c0c0">2</MedalTag>
                </>
              ) : (
                <PlaceholderText>🥈 2</PlaceholderText>
              )}
            </PodiumItem>

            <PodiumItem rank="1">
              {topThree[0] ? (
                <>
                  {renderUser(topThree[0])}
                  <MedalTag color="#ffd700">1</MedalTag>
                </>
              ) : (
                <PlaceholderText>🥇 1</PlaceholderText>
              )}
            </PodiumItem>

            <PodiumItem rank="3">
              {topThree[2] ? (
                <>
                  {renderUser(topThree[2])}
                  <MedalTag color="#cd7f32">3</MedalTag>
                </>
              ) : (
                <PlaceholderText>🥉 3</PlaceholderText>
              )}
            </PodiumItem>

          </PodiumContainer>

          <ScrollWrapper scrollable={viewMoreLB}>
            {leaderboard.length === 0 ? (
              <EmptyLeaderboard>
                <p>No participants yet.</p>
                <p>Be the first to join and appear here!</p>
              </EmptyLeaderboard>
            ) : (
              <LeaderboardList>
                {/* Pinned current user row if rank > 3 */}
                {(() => {
                  const currentUserUid = userUid;
                  const yourRankEntry = leaderboard.find(
                    (member) => member.user_uid === currentUserUid
                  );
                  const shouldShowYourRank = yourRankEntry && yourRankEntry.rank > 3;

                  if (shouldShowYourRank) {
                    return (
                      <StyledYourRankRow>
                        <UserRow>
                          {yourRankEntry.icon_image ? (
                            <UserAvatar src={`data:image/jpeg;base64,${yourRankEntry.icon_image}`} />
                          ) : (
                            <FallbackAvatar>{yourRankEntry.user_uid?.charAt(0).toUpperCase() || "U"}</FallbackAvatar>
                          )}
                          <span>#{yourRankEntry.rank}. {yourRankEntry.user_name} (You)</span>
                        </UserRow>
                        <MetricWithStatus>
                          <span>{yourRankEntry.user_challenge_status_metrics} {challenge.metric_type}</span>
                          {yourRankEntry.user_challenge_status === "completed" ? (
                            <CompletionBadge>✔</CompletionBadge>
                          ) : (
                            <StatusIcon color="orange">⏳</StatusIcon>
                          )}
                        </MetricWithStatus>
                      </StyledYourRankRow>
                    );
                  }
                  return null;
                })()}

                {/* Remaining leaderboard entries (excluding top 3) */}
                {leaderboard
                  .filter((entry) => entry.rank > 3 && entry.user_uid !== userUid)
                  .slice(0, viewMoreLB ? leaderboard.length : 5)
                  .map((entry, i) => {
                    const isCompleted = entry.user_challenge_status === "completed";
                    return (
                      <LeaderboardItem key={i}>
                        <UserRow>
                          {entry.icon_image ? (
                            <UserAvatar src={`data:image/jpeg;base64,${entry.icon_image}`} />
                          ) : (
                            <FallbackAvatar>{entry.user_uid?.charAt(0).toUpperCase() || "U"}</FallbackAvatar>
                          )}
                          <span>{entry.rank}. {entry.user_name}</span>
                        </UserRow>
                        <MetricWithStatus>
                          <span>{entry.user_challenge_status_metrics} {challenge.metric_type}</span>
                          {isCompleted ? (
                            <CompletionBadge>✔</CompletionBadge>
                          ) : (
                            <StatusIcon color="orange">⏳</StatusIcon>
                          )}
                        </MetricWithStatus>
                      </LeaderboardItem>
                    );
                  })}

                {/* Ghost entries to fill up to 6 rows if needed */}
                {[...Array(Math.max(0, 6 - leaderboard.length))].map((_, i) => {
                  const fakeRank = leaderboard.length + i + 1;
                  return (
                    <LeaderboardItem key={`ghost-${i}`} style={{ opacity: 0.3 }}>
                      <UserRow>
                        <FallbackAvatar>?</FallbackAvatar>
                        <span>{fakeRank}. Waiting for participant...</span>
                      </UserRow>
                      <span>-- {challenge.metric_type}</span>
                    </LeaderboardItem>
                  );
                })}
              </LeaderboardList>
            )}

            {leaderboard.filter((entry) => entry.rank > 3 && entry.user_uid !== userUid).length > 5 && (
              <Toggle onClick={() => setViewMoreLB(!viewMoreLB)}>
                {viewMoreLB ? "Collapse" : "View More"}
              </Toggle>
            )}
          </ScrollWrapper>



        </RightSection>

        {showConfirmModal && (
          <ModalOverlay>
            <ModalBox>
              <h2>Wait... you're so close!</h2>
              {userProgress >= challenge.goal_count ? (
                <p>
                  🎉 You've already completed the challenge. Your rewards might be waiting!
                  Don't miss out by quitting now.
                </p>
              ) : (
                <p>
                  {motivationalMessages[motivIndex](
                    Math.max(0, challenge.goal_count - userProgress),
                    challenge.metric_type
                  )}
                </p>
              )}
              <ModalButtons>
                <button onClick={() => setShowConfirmModal(false)}>Continue Challenge</button>
                <button className="danger" onClick={async () => {
                  const payload = [{
                    challenge_uid: challenge.challenge_uid,
                    org_uid: orgUid,
                    user_uid: userUid,
                  }];

                  try {
                    await axios.delete(`${process.env.REACT_APP_API_URL}/api/quit-challenge`, {
                      headers: {
                        accept: "application/json",
                        "user-uid": userUid,
                      }, withCredentials: true,
                      data: payload,
                    });

                    setShowConfirmModal(false);
                    openNotification({
                      type: "success",
                      message: "Left Challenge",
                    });
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);

                  } catch (error: any) {
                    console.error("Failed to quit challenge:", error);

                    const errorMessage =
                      error?.response?.data?.message || "Something went wrong. Please try again.";

                    openNotification({
                      type: "error",
                      message: errorMessage,
                    });
                  }

                }}>
                  Yes, Quit Anyway
                </button>
              </ModalButtons>
            </ModalBox>
          </ModalOverlay>
        )}

      </Wrapper>
    </>

  );

};

export default ChallengeDetailPage;

const Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  padding: 10px;
  justify-content: space-between;
  
  @media (max-width: 768px) {
  flex-direction: column;
}
`;
const TopSectionRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AvatarImg = styled.img`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  margin: auto;
`;


const LeftSection = styled.div`
  flex: 1 1 48%;
`;
const RightSection = styled.div`
  flex: 1 1 48%;
  background: #fbfbfb;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.03);
`;

const Title = styled.h1`
  font-size: 1.2rem;
`;

const Title2 = styled.h1`
  font-size: 1rem;
  margin:0px;
`;
const Title0 = styled.h1`
  font-size: 1.3rem;
  margin:0px;
  margin-left:10px;
  margin-top:5px;
`;
const Image = styled.img`
  width: 100%;
  border-radius: 12px;
  margin: 12px 0;
`;

const Description = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #444;
  margin: 0;
`;

const Toggle = styled.span`
  color: #007bff;
  margin-left: 8px;
  cursor: pointer;
  font-weight: 500;
`;

const QuitButton = styled.button`
  background: transparent;
  color: #e74c3c;
  border: 2px solid #e74c3c;
  border-radius: 6px;
  padding: 10px 16px;
  margin-top: 20px;
  font-weight: bold;
  transition: all 0.3s;
  &:hover {
    background: #e74c3c;
    color: white;
  }
`;
const JoinButton = styled.button`
  padding: 10px 20px;
  background-color: #27ae60; /* vibrant green */
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #1e874b; /* darker green on hover */
  }

  &:disabled {
    background-color: #b2dfb2;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 10px;
`;

const TopThree = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
`;


const Avatar = styled.div`
  width: 36px;
  height: 36px;
  margin: auto;
  border-radius: 50%;
  background: #007bff;
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #eaeaea;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px;
`;

const RotatingGear = styled(Settings)`
  animation: ${spin} 1s linear infinite;
`;

const LoaderText = styled.div`
  margin-top: 8px;
`;


const TopCard = styled.div`
  flex: 1;
  background: #f1f1f1;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
`;


const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LeaderboardItem = styled.div`
  background: #f8f8f8;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: 0.3s ease;
`;

const ChallengeImageWrapper = styled.div`
  position: relative;
  width: 550px;
  height: 300px;
  overflow: hidden;
  border-radius: 12px;
  margin: 16px 0;
   flex: 1 1 50%;
`;

const ChallengeImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.85);
`;
const ImageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 12px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  color: white;
  font-weight: bold;
`;

const PodiumContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 16px;
  margin: 24px 0;
`;

const PodiumItem = styled.div<{ rank: string }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  padding: 12px;
  border-radius: 12px;
  background: ${({ rank }) =>
    rank === '1' ? '#ffeaa7' : rank === '2' ? '#dfe6e9' : '#fab1a0'};
  height: ${({ rank }) =>
    rank === '1' ? '150px' : '120px'};
  flex: 1;
  text-align: center;
  white-space: normal;
  word-break: break-word;
  max-width: 120px;
  overflow: visible; /* important for medal to overflow */
`;



const ScrollWrapper = styled.div<{ scrollable: boolean }>`
  max-height: ${({ scrollable }) => (scrollable ? "450px" : "100%")};
  overflow-y: ${({ scrollable }) => (scrollable ? "auto" : "visible")};
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.img`
  width: 62px;
  height: 62px;
  border-radius: 50%;
`;

const FallbackAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #007bff;
  color: white;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const MetaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 5px;
`;

const MetaCard = styled.div`
  background: #f5f5f5;
  padding: 12px;
  border-radius: 10px;
  text-align: center;
  font-weight: 500;
  font-size: 14px;
`;

const ProgressCard = styled(MetaCard)`
  padding: 24px;
`;

const ProgressLabel = styled.div`
  margin-top: 8px;
  font-size: 12px;
  color: #666;
`;

const CountdownCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff7e6;
  color: #e67e22;
  border: 1.5px dashed #e67e22;
  font-weight: 600;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 15px;
  width: fit-content;
  animation: pulse 2s infinite;
  margin-top: 8px;

  span {
    margin-right: 8px;
  }
`;


const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;
const UserProgressContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-radius: 12px;
  background: #f9f9f9;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-top: 5px;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Username = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const UserMetric = styled.div`
  font-size: 14px;
  color: #666;
`;

const MiniProgressWrapper = styled.div`
  width: 60px;
  height: 60px;
`;
const Description_Timer_Wrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #fefefe;
  border: 1px solid #eee;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
  min-width: 250px;

  @media (max-width: 768px) {
    padding: 12px;
    margin-top: 16px;
  }
`;

const ChallengeBadge = styled.div`
  position: absolute;
  top: 12px;
  color: white;
  padding: 6px 12px;
  border-radius: 8px;
  font-family: sans-serif;
  font-size: 1.5rem;
  font-weight: bold;
  z-index: 1;
`;
const StatusTag = styled.div<{ status: "upcoming" | "live" | "ended" }>`
  position: absolute;
  bottom: 12px;
  right: 12px;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  border-radius: 8px;
  background-color: ${({ status }) =>
    status === "upcoming" ? "#f1c40f" :
      status === "live" ? "#27ae60" :
        "#e74c3c"};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
`;
const JoinPlaceholder = styled.div`
  margin-top: 8px;
  margin-bottom: 10px;
  padding: 16px;
  background: #f5faff;
  border: 2px dashed #5dece7;
  color: #094752;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 500;
  text-align: center;
`;
const PlaceholderText = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  color: #aaa;
  text-align: center;
  opacity: 0.6;
`;

const EmptyLeaderboard = styled.div`
  padding: 24px;
  text-align: center;
  border-radius: 12px;
  color: #888;
  font-size: 0.95rem;
  line-height: 1.6;
`;
const MetricWithStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusIcon = styled.span<{ color: string }>`
  font-size: 1rem;
  color: ${({ color }) => color};
`;
const CompletionBadge = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid #2ecc71; /* green border */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2ecc71; /* green tick */
  font-size: 14px;
  font-weight: bold;
`;
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalBox = styled.div`
  background: white;
  padding: 30px 24px;
  border-radius: 16px;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  text-align: center;

  h2 {
    margin-bottom: 12px;
    color: #2c3e50;
  }

  p {
    font-size: 1rem;
    color: #555;
  }
`;

const ModalButtons = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;

  button {
    flex: 1;
    padding: 10px 14px;
    margin: 0 6px;
    border: none;
    border-radius: 8px;
    font-weight: bold;
    font-size: 0.95rem;
    cursor: pointer;
    transition: background 0.3s ease;

    &:first-child {
      background: #f0f0f0;
      color: #2c3e50;
    }

    &.danger {
      background: #e74c3c;
      color: white;
    }
  }
`;
const MedalTag = styled.div<{ color: string }>`
  position: absolute;
  bottom: -14px; /* pushes it halfway outside */
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  background-color: ${({ color }) => color};
  color: #fff;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: bold;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  z-index: 2;
`;

const CountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 18px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0f2f1, #f1f8e9);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.06);
  margin: 16px 0;
  font-family: "Segoe UI", "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  transition: 0.3s ease all;

  &:hover {
    transform: scale(1.01);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  }
`;

const CountdownLabel = styled.span`
  font-weight: 600;
  color: #00796b;
  margin-right: 6px;
`;

const CountdownText = styled.span`
  color: #37474f;
`;
const StyledYourRankRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #e8f5e9;
  padding: 12px 16px;
  margin-bottom: 14px;
  border-radius: 12px;
  border: 1px solid #c5e1a5;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);

  .rank {
    font-size: 16px;
    color: #00796b;
  }

  .name {
    flex: 1;
    margin-left: 10px;
    color: #004d40;
  }

  .points {
    color: #00796b;
  }
`;